import {Observable, timer} from 'rxjs';

/**
 * A Map implementation that returns a default value if a key is not found.
 */
export class MapWithDefault<T, S> extends Map<T, S> {
  private readonly default: S;

  constructor(entries, defaultValue) {
    super(entries);
    this.default = defaultValue;
  }

  override get(key): S {
    if (!super.has(key)) {
      super.set(key, this.default);
    }
    return super.get(key)!;
  }

  toMap(): Map<T, S> {
    return new Map<T, S>([...this.entries()]);
  }
}

/** A type guard which ensures that provided value is truthy. */
export function isTruthy<T>(value: T): value is Exclude<T, '' | 0 | false | null | undefined> {
  return Boolean(value);
}

/**
 * Converts an hour number (0-23) to a 24-hour format string (e.g., "09:00", "14:00").
 */
export function toHourFormat(hour: number): string {
  return `${hour.toString().padStart(2, '0')}:00`;
}

/**
 * Formats a number to a fixed number of decimal places, handling undefined input by treating it as 0.
 */
export function toFixedNumber(value: number | undefined, digits = 2): string {
  return Number(value ?? 0).toFixed(digits);
}

/**
 * Extracts the decimal/fractional part of a number as a string.
 * If the number is an integer or has no decimal part, it returns an empty string.
 * @param value The number from which to extract the decimal part.
 * @returns The decimal part of the number as a string, or an empty string if no decimal part
 * exists.
 */
export function extractDecimalPart(value: number): string {
  return value.toString().split('.')[1] ?? '';
}

/**
 * Calculates the sum of values at each corresponding index across all number arrays within a Map.
 * Assumes that all number arrays in the Map have the same length.
 * The Map's keys are strings, and its values are arrays of numbers.
 *
 * @param map A Map where keys are strings and values are arrays of numbers.
 * If the Map is null or empty, an empty array is returned.
 * @returns An array of numbers where each element represents the sum of values
 * at the same index across all the number arrays in the input Map.
 * Returns an empty array if the input Map is null or empty.
 *
 * @example
 * ```typescript
 * const dataMap = new Map<string, number[]>([
 * ["a", [1, 2, 3]],
 * ["b", [4, 5, 6]],
 * ["c", [7, 8, 9]],
 * ]);
 *
 * const sumResult = sumMapValuesAtIndex(dataMap);
 * console.log(sumResult); // Output: [12, 15, 18] (1+4+7, 2+5+8, 3+6+9)
 *
 * ```
 */
export function sumMapValuesAtIndex(map: Map<string, number[]>): number[] {
  if (!map || map.size === 0) {
    return [];
  }

  return Array.from(map.values()).reduce((result: number[], currentArray: number[]): number[] => {
    if (result.length === 0) {
      return currentArray;
    }
    return result.map((value: number, index: number) => value + currentArray[index]);
  }, []);
}

/**
 * Calculates the average of values at each corresponding index across all number arrays within a Map.
 * Assumes that all number arrays in the Map have the same length.
 * The Map's keys are strings, and its values are arrays of numbers.
 *
 * @param map A Map where keys are strings and values are arrays of numbers.
 * If the Map is null or empty, an empty array is returned.
 * @returns An array of numbers where each element represents the average of values
 * at the same index across all the number arrays in the input Map.
 * Returns an empty array if the input Map is null or empty.
 *
 * @example
 * ```typescript
 * const dataMap = new Map<string, number[]>([
 * ["a", [1, 2, 3]],
 * ["b", [4, 5, 6]],
 * ["c", [7, 8, 9]],
 * ]);
 *
 * const avgResult = avgMapValuesAtIndex(dataMap);
 * console.log(avgResult); // Output: [4, 5, 6] ((1+4+7)/3, (2+5+8)/3, (3+6+9)/3)
 *
 * ```
 */
export function avgMapValuesAtIndex(map: Map<string, number[]>): number[] {
  if (!map || map.size === 0) {
    return [];
  }

  const sumResult = sumMapValuesAtIndex(map);
  const mapSize = map.size;

  return sumResult.map((value: number) => value / mapSize);
}

/**
 * Groups items by their 'type' and aggregates their 'values' at each index using a provided aggregation function.
 * Assumes that all 'values' arrays within the items have the same and equal length.
 *
 * @template T The type of the items in the input array.
 * @template K The type of the key used for grouping.
 * @template V The type of the individual values within the 'values' array.
 *
 * @param items An array of items to process. Each item should have a property that can be used as a 'type' (for grouping)
 * and a property that is an array of 'values' to be aggregated.
 * @param typeSelector A function that takes an item of type `T` and returns the key of type `K` by which the items should be grouped.
 * This function defines how the 'type' is extracted from each item.
 * @param valuesSelector A function that takes an item of type `T` and returns an array of values of type `V`.
 * This function defines how the array of 'values' to be aggregated is extracted from each item.
 * @param aggregator A function that takes the existing aggregated value (of type `V` or `undefined` for the first item of a type at a given index)
 * and the current value (of type `V`) at the same index, and returns the updated aggregated value (of type `V`).
 * This function defines the aggregation logic (e.g., sum, average, min, max).
 *
 * @returns A Map where the key is the 'type' (of type `K`) and the value is an array of the aggregated 'values' (of type `V[]`)
 * for all items of that type at each corresponding index. The length of each aggregated 'values' array will be the
 * same as the length of the 'values' arrays in the input items.
 *
 * @example
 * ```typescript
 * // Define the data structure
 * interface DataItem {
 *  category: string;
 *  measurements: number[];
 * }
 *
 * // Sample data
 * const data: DataItem[] = [
 *  { category: 'A', measurements: [10, 20, 30] },
 *  { category: 'B', measurements: [5, 10, 15] },
 *  { category: 'A', measurements: [15, 25, 35] },
 *  { category: 'B', measurements: [7, 12, 17] },
 * ];
 *
 * // Use the aggregateValuesByTypeAtIndex function to sum the measurements by category
 * const sumByCategory = aggregateValuesByTypeAtIndex(
 *  data,
 *  (item) => item.category,
 *  (item) => item.measurements,
 *  (existing, current) => (existing === undefined ? current : existing + current)
 * );
 *
 * console.log(sumByCategory);
 * // Expected Output:
 * // Map(2) {
 * //   'A' => [ 25, 45, 65 ],
 * //   'B' => [ 12, 22, 32 ]
 * // }
 * ```
 */
export function aggregateValuesByTypeAtIndex<T, K, V>(
  items: T[],
  typeSelector: (item: T) => K,
  valuesSelector: (item: T) => V[],
  aggregator: (existing: V | undefined, current: V) => V,
): Map<K, V[]> {
  const result = new Map<K, V[]>();

  for (const item of items) {
    const type: K = typeSelector(item);
    const values: V[] = valuesSelector(item);

    if (result.has(type)) {
      const existingAggregatedValues: V[] = result.get(type)!;
      const aggregatedValues: V[] = [];
      for (let i = 0; i < values.length; i++) {
        aggregatedValues.push(aggregator(existingAggregatedValues[i], values[i]));
      }
      result.set(type, aggregatedValues);
    } else {
      result.set(type, [...values]); // Create a copy of the initial values.
    }
  }

  return result;
}

/**
 * Parses a string that may contain non-numeric characters into a number.
 * This function strips out any characters that are not digits, a decimal point, or a minus sign,
 * then attempts to parse the remaining string as a float.
 *
 * @param value The string to parse. It can be undefined or contain currency symbols, commas, etc.
 * @returns The parsed number. If the input is falsy (null, undefined, empty string) or if the
 *          cleaned string cannot be parsed into a valid number (e.g., results in NaN), it returns 0.
 * @example
 * parseNumericValue('$1,234.56') // returns 1234.56
 * parseNumericValue(' -50%') // returns -50
 * parseNumericValue() // returns 0
 */
export function parseNumericValue(value?: string): number {
  if (!value) {
    return 0;
  }
  const parsed = parseFloat(value.replace(/[^\d.-]/g, ''));
  return isNaN(parsed) ? 0 : parsed;
}

/**
 * Removes extension from a file name, if available.
 *
 * This function identifies an extension in the file name as substring that starts with the last
 * dot (`.`) and continues to the end of the string, provided that there are no slashes in the
 * substring.
 *
 * Note: In case the input `fileName` lacks an extension but includes a '.'
 * (for e.g., "test.file.name"), this function may result in the last dot portion of the name
 * getting removed (i.e., "test.file").
 *
 * @param fileName The name of the file.
 * @returns The name of the file with file extension removed if it exists.
 *
 * @example
 * getFileNameWithoutExtension("img.jpg") // returns "img"
 * getFileNameWithoutExtension("No file extension") // returns "No file extension"
 * getFileNameWithoutExtension("test.file.name")  // returns "test.file"
 */
export function getFileNameWithoutExtension(fileName: string): string {
  return fileName.replace(/\.[^/.]+$/, '');
}

/**
 * Defines CSS classes for different density levels, to be kept in sync with styles defined in
 * `material_theme.scss`.
 * These classes are applied to elements to adjust their visual density (e.g., spacing, padding).
 */
export enum DensityCssClass {
  DEFAULT = '',
  SLIGHTLY_COMPACT = 'slightly-compact-density',
  COMPACT = 'compact-density',
  VERY_COMPACT = 'very-compact-density',
  ULTRA_COMPACT = 'ultra-compact-density',
  EXTREMELY_COMPACT = 'extremely-compact-density',
}

/**
 * Calculates a retry delay with exponential backoff, capped by a maximum wait time.
 *
 * This function can be used in retry mechanisms where you want to gradually increase the
 * waiting time between retries, but not exceed a certain limit.
 *
 * @param retryCount The current number of retries attempted (1 for the first retry, 2 for the second, etc.).
 * @param pollingIntervalMs The base polling interval in milliseconds. This value is multiplied by the backoff factor.
 * @param maxWaitMs The maximum waiting time in milliseconds. The calculated delay will not exceed this value.
 * @returns An Observable that emits a single value (the calculated delay) after the specified time.
 */
export function getRetryDelay(
  retryCount: number,
  pollingIntervalMs: number,
  maxWaitMs: number,
): Observable<number> {
  // Exponential backoff.
  const backoffFactor = Math.pow(2, retryCount - 1);
  const calculatedDelay = backoffFactor * pollingIntervalMs;

  // The user never waits longer than [maxWaitMs] between checks.
  const finalDelay = Math.min(calculatedDelay, maxWaitMs);

  return timer(finalDelay);
}
